// Data migration home for the pawn module — /pawn/import-data.
//
// A shop moving off its old on-prem software has five things to bring across,
// and they have to arrive in a fixed order: customers, then the accounts their
// money moved through, then the loans that belong to them, then the receipts
// taken on those loans, then the loans that were already closed. Get that order
// wrong and a loan has nobody to belong to, or no account to be paid out from.
//
// So the screen IS the order: five numbered cards, read top-left to bottom-right,
// each opening the same four-step popup (template → upload → check → import).
// The customer card hands off to the Pawn Customer Master's existing bulk
// import — that flow is already built and proven, and a second copy of it would
// only be a worse one. The other four run the generic, spec-driven dialog.
//
// All five run end to end. Every popup offers a rehearsal first ("Check first"),
// which does every lookup and every balance test and creates nothing — worth
// insisting on, because these rows land in the ledger.
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CloudUploadRounded as ImportIcon,
  DownloadRounded as DownloadIcon,
  GroupsRounded as CustomersIcon,
  AccountBalanceRounded as AccountsIcon,
  PaymentsRounded as LoansIcon,
  ReceiptLongRounded as ReceiptsIcon,
  TaskAltRounded as SettlementsIcon,
  ArrowForwardRounded as ArrowIcon,
  ConstructionRounded as DraftIcon,
  CheckCircleRounded as LiveIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";

import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { usePawnMode } from "../../Context/PawnModeContext";
import PawnNavButtons from "./pawnNavButtons";
import { API_BASE_URL } from "./pawnCustomer/shared";
import BulkCustomerDialog from "./pawnCustomer/BulkCustomerDialog";
import ImportSheetDialog from "./importData/ImportSheetDialog";
import { IMPORT_SPECS } from "./importData/importSpecs";
import { downloadTemplate } from "./importData/importSheet";

// ─── Blue/White design tokens (shared across the pawn screens) ───────────────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const INK = "#0F172A";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const BG_ALT = "#F1F5F9";

// Icons live here, not in the specs — those stay plain data.
const ICONS = {
  customers: <CustomersIcon />,
  accounts: <AccountsIcon />,
  loans: <LoansIcon />,
  receipts: <ReceiptsIcon />,
  settlements: <SettlementsIcon />,
};

// What a spreadsheet cannot carry across, said once and plainly. Every one of
// these has bitten a migration somewhere: the shop assumes the old data is all
// here, and only notices the gap when a customer asks for their photo.
const NOT_IMPORTED = [
  "Photos, signatures, fingerprints and scanned documents — open the record afterwards to attach them.",
  "Masters: schemes, metal types, purities and floors. Set those up first; the sheets refer to them by name.",
  "Opening balances and past ledger entries — bring those in through Cloud Accounting, not from here.",
  "Printed tickets and barcode labels. Nothing is printed by an import; reprint from the loan when you need one.",
];

const StatusChip = ({ status }) =>
  status === "live" ? (
    <Chip
      size="small"
      icon={<LiveIcon sx={{ fontSize: "16px !important" }} />}
      label="Ready to import"
      sx={{
        height: 22,
        fontSize: "0.68rem",
        fontWeight: 700,
        color: "#15803D",
        bgcolor: "#ECFDF5",
        border: "1px solid #A7F3D0",
        "& .MuiChip-icon": { color: "#15803D" },
      }}
    />
  ) : (
    <Tooltip title="Download the template and check your sheet now — the create step is being built">
      <Chip
        size="small"
        icon={<DraftIcon sx={{ fontSize: "16px !important" }} />}
        label="Template ready"
        sx={{
          height: 22,
          fontSize: "0.68rem",
          fontWeight: 700,
          color: "#B45309",
          bgcolor: "#FFFBEB",
          border: "1px solid #FDE68A",
          "& .MuiChip-icon": { color: "#B45309" },
        }}
      />
    </Tooltip>
  );

// ─── One dataset card ─────────────────────────────────────────────────────────
const DatasetCard = ({ spec, onOpen }) => (
  <Box
    onClick={() => onOpen(spec)}
    sx={{
      display: "flex",
      flexDirection: "column",
      bgcolor: "#fff",
      border: `1px solid ${BORDER}`,
      borderRadius: 3,
      p: { xs: 1.75, md: 2 },
      cursor: "pointer",
      transition:
        "box-shadow .2s ease, border-color .2s ease, transform .2s ease",
      "&:hover": {
        borderColor: `${BLUE}66`,
        boxShadow: "0 10px 24px rgba(15,23,42,0.10)",
        transform: "translateY(-2px)",
      },
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      {/* Step number doubles as the icon plinth — the order is the whole point
          of this screen, so it is never more than a glance away. */}
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            bgcolor: BLUE,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
          }}
        >
          {ICONS[spec.key]}
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: -6,
            left: -6,
            width: 20,
            height: 20,
            borderRadius: "50%",
            bgcolor: INK,
            color: "#fff",
            fontSize: "0.68rem",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {spec.step}
        </Box>
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: "0.98rem", color: INK }}>
          {spec.title}
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          <StatusChip status={spec.status} />
        </Box>
      </Box>
    </Stack>

    <Typography
      sx={{ mt: 1.25, fontSize: "0.82rem", color: INK_MUTED, lineHeight: 1.5 }}
    >
      {spec.blurb}
    </Typography>

    <Typography sx={{ mt: 1, fontSize: "0.74rem", color: INK_MUTED }}>
      <b>One row</b> = {spec.rowMeaning}.
    </Typography>

    <Box sx={{ flex: 1 }} />

    <Stack
      direction="row"
      spacing={0.75}
      sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${BORDER}` }}
      alignItems="center"
    >
      <Chip
        size="small"
        variant="outlined"
        label={`${spec.columns.length} columns`}
        sx={{ height: 22, fontSize: "0.68rem", color: INK_MUTED }}
      />
      <Box sx={{ flex: 1 }} />
      <Button
        size="small"
        startIcon={<DownloadIcon />}
        onClick={(e) => {
          e.stopPropagation(); // the card itself opens the popup
          if (spec.downloadTemplate) spec.downloadTemplate();
          else downloadTemplate(spec);
        }}
        sx={{ textTransform: "none", fontWeight: 600, color: BLUE }}
      >
        Template
      </Button>
      <Button
        size="small"
        variant="contained"
        endIcon={<ArrowIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 2,
          bgcolor: BLUE,
          "&:hover": { bgcolor: BLUE_DARK },
        }}
      >
        Import
      </Button>
    </Stack>
  </Box>
);

const PawnImportData = () => {
  const { currentStoreInfo, userInfo } = useContext(AutoTaskAuthContext) || {};
  const { isUnofficial } = usePawnMode() || {};

  const storeId = currentStoreInfo?.id;
  const storeName = currentStoreInfo?.name;

  // Which dataset's popup is open. The customer one is a different dialog, so
  // the key decides which of the two renders — not a shared `open` flag.
  const [activeKey, setActiveKey] = useState(null);
  const active = useMemo(
    () => IMPORT_SPECS.find((s) => s.key === activeKey) || null,
    [activeKey]
  );

  // Only the customer import needs it: without the store's duplicate-mobile
  // setting the dialog cannot know whether to offer the override switch.
  const [allowDuplicateMobile, setAllowDuplicateMobile] = useState(false);
  useEffect(() => {
    if (storeId === undefined || storeId === null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/settings/${storeId}`
        );
        if (!cancelled && res.data?.success && res.data.data)
          setAllowDuplicateMobile(
            Boolean(res.data.data.allow_duplicate_mobile)
          );
      } catch (err) {
        console.error("Error loading pawn settings:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const downloadEveryTemplate = () =>
    IMPORT_SPECS.forEach((spec) =>
      spec.downloadTemplate ? spec.downloadTemplate() : downloadTemplate(spec)
    );

  return (
    <Box
      sx={{
        height: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: BG_ALT,
        p: { xs: 1.25, sm: 1.5, md: 2.5 },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 1.5 },
          mb: { xs: 1.5, md: 2 },
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        <PawnNavButtons />
        <Box
          sx={{
            width: { xs: 36, sm: 42 },
            height: { xs: 36, sm: 42 },
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: BLUE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
          }}
        >
          <ImportIcon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.05rem", sm: "1.25rem" },
              color: INK,
            }}
          >
            Import Data
          </Typography>
          <Typography noWrap sx={{ fontSize: "0.75rem", color: INK_MUTED }}>
            {storeName
              ? `${storeName} · Bring your old software's records in from Excel`
              : "Bring your old software's records in from Excel"}
          </Typography>
        </Box>
      </Box>

      {/* ── What this screen is, and the order it has to happen in ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          p: { xs: 1.75, md: 2.25 },
          mb: { xs: 1.5, md: 2 },
          flexShrink: 0,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 1.5, md: 2 }}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 800, fontSize: "0.95rem", color: INK }}
            >
              Moving in from another pawn software?
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: "0.82rem",
                color: INK_MUTED,
                lineHeight: 1.55,
              }}
            >
              Export each kind of record from the old software, paste it into
              our template, and upload it here. Every sheet is checked in full
              and shown back to you before a single record is created — and the
              order below matters: a loan can only be attached to a customer who
              is already in, and a receipt only to a loan that is already in.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadEveryTemplate}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              whiteSpace: "nowrap",
              borderColor: BLUE,
              color: BLUE,
              flexShrink: 0,
            }}
          >
            Download all 5 templates
          </Button>
        </Stack>

        {/* The running order, as a line you can read in one breath. */}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1.5 }}
        >
          {IMPORT_SPECS.map((spec, i) => (
            <React.Fragment key={spec.key}>
              {i > 0 && <ArrowIcon sx={{ fontSize: 14, color: INK_MUTED }} />}
              <Chip
                size="small"
                label={`${spec.step}. ${spec.label}`}
                onClick={() => setActiveKey(spec.key)}
                sx={{
                  height: 24,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: BLUE,
                  bgcolor: `${BLUE}12`,
                  border: `1px solid ${BLUE}33`,
                }}
              />
            </React.Fragment>
          ))}
          <Typography sx={{ fontSize: "0.72rem", color: INK_MUTED, ml: 0.5 }}>
            Settlements are optional — skip them if you only need the loans
            still running.
          </Typography>
        </Stack>
      </Box>

      {isUnofficial && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: 2, flexShrink: 0 }}
        >
          You are in the <b>unofficial</b> book. Anything imported now lands
          there. Switch back before importing records that belong to the
          official one.
        </Alert>
      )}

      {/* ── The five datasets ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(5, minmax(0, 1fr))",
          },
          gap: { xs: 1.25, md: 2 },
          flexShrink: 0,
        }}
      >
        {IMPORT_SPECS.map((spec) => (
          <DatasetCard
            key={spec.key}
            spec={spec}
            onOpen={(s) => setActiveKey(s.key)}
          />
        ))}
      </Box>

      {/* ── What a spreadsheet cannot bring across ── */}
      <Box
        sx={{
          mt: { xs: 1.5, md: 2 },
          bgcolor: "#fff",
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          p: { xs: 1.75, md: 2.25 },
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <InfoIcon sx={{ fontSize: 18, color: BLUE }} />
          <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: INK }}>
            What an import does not bring across
          </Typography>
        </Stack>
        <Divider sx={{ my: 1.25 }} />
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {NOT_IMPORTED.map((line) => (
            <Box
              component="li"
              key={line}
              sx={{
                fontSize: "0.8rem",
                color: INK_MUTED,
                lineHeight: 1.6,
                mb: 0.5,
              }}
            >
              {line}
            </Box>
          ))}
        </Box>
        <Typography
          sx={{ mt: 0.5, fontSize: "0.8rem", color: INK, fontWeight: 600 }}
        >
          Keep the old software readable until you have checked the imported
          loans against it — an import never edits or deletes anything already
          here, so a sheet can always be corrected and the extra rows
          re-imported.
        </Typography>
      </Box>

      {/* ── Popups ──
          Customers reuse the Customer Master's bulk import; the other three run
          the generic spec-driven dialog. */}
      <BulkCustomerDialog
        open={activeKey === "customers"}
        onClose={() => setActiveKey(null)}
        storeId={storeId}
        createdBy={userInfo?.id}
        allowDuplicateMobile={allowDuplicateMobile}
      />
      <ImportSheetDialog
        open={!!active && active.key !== "customers"}
        spec={active && active.key !== "customers" ? active : null}
        onClose={() => setActiveKey(null)}
        storeName={storeName}
        isUnofficial={!!isUnofficial}
        storeId={storeId}
        userId={userInfo?.id ?? null}
        pawnMode={isUnofficial ? 2 : 1}
      />
    </Box>
  );
};

export default PawnImportData;
